/* Puzzle data + analysis. The analysis is a pure function of the canonical
   sentence: it indexes the letter positions, identifies the pivot (for odd-
   letter palindromes), and exposes the mirror function the cursor relies on.

   The shape mirrors the api.md `PuzzlePublic` wire payload, modulo
   case-style: the wire uses snake_case (max_guesses, solution_normalized),
   we expose camelCase here. `web/src/game/api.ts::mapWireToPuzzle` is the
   adapter at the boundary.

   `text` is the canonical name (matches the wire and lambda/shared); we
   carry `sentence` as a synonym of `text` so existing UI components and
   tests that read `puzzle.sentence` continue to work without a sweeping
   rename. New code should prefer `text`. */

export type Difficulty = 1 | 2 | 3 | 4 | 5;

export type Puzzle = {
  /** Stable identifier; aligns with api.md "puzzle.number". */
  number: number;
  /** ISO YYYY-MM-DD release date in UTC. */
  date: string;
  /** Punctuated palindrome shipped to the player. Source of truth for
     analyzePuzzle and the solution-reveal screen. */
  text: string;
  /** Synonym of `text`. Existing components (Hint, ShareGrid, etc.)
     read `puzzle.sentence`; preserved here as defensive alias so the
     rename to `text` doesn't ripple. New code should prefer `text`. */
  sentence: string;
  /** Punctuated mask: letters → "_", everything else passes through. */
  mask: string;
  /** Letter count (count of `_` in mask). Redundant with mask but
     ergonomic for client-side validation. */
  letters: number;
  /** Letters-only lowercase canonical answer. */
  solutionNormalized: string;
  /** Public-facing one-liner attribution (CSS shows it under the answer). */
  byline: string;
  /** Hint text revealed when the player buys it. */
  hint: string;
  /** Total number of guess slots (rows on the board). */
  maxGuesses: number;
  /** Optional difficulty rating, when sourced from the curation pipeline.
     Wire payload omits it; defaults to 3 in the adapter. */
  difficulty?: Difficulty;
};

export type PuzzleAnalysis = {
  sentence: string;
  /** Lowercased letters-only form. Used for scoring. */
  normalized: string;
  /** Indices into `sentence` (in order) where chars are letters. */
  letterIndices: readonly number[];
  /** Total number of letter slots. */
  letterCount: number;
  /** Letter indices marking the symmetry center: 1 element for odd-length
     palindromes, 2 (the two center letters) for even-length. */
  pivotLetterIndices: readonly number[];
  /** Maximum left-cursor position (inclusive). pivot if odd, mid-1 if even. */
  cursorMax: number;
};

const LETTER_RE = /[a-z]/i;

export function analyzePuzzle(p: Puzzle): PuzzleAnalysis {
  const text = p.text;
  const indices: number[] = [];
  let normalized = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (LETTER_RE.test(ch)) {
      indices.push(i);
      normalized += ch.toLowerCase();
    }
  }

  const letterCount = indices.length;
  const reversed = normalized.split("").reverse().join("");
  if (normalized !== reversed) {
    throw new Error(`Puzzle text is not a palindrome: ${text}`);
  }

  const isOdd = letterCount % 2 === 1;
  const pivotLetterIndices = isOdd
    ? [(letterCount - 1) / 2]
    : [letterCount / 2 - 1, letterCount / 2];
  const cursorMax = isOdd ? pivotLetterIndices[0]! : letterCount / 2 - 1;

  return {
    sentence: text,
    normalized,
    letterIndices: indices,
    letterCount,
    pivotLetterIndices,
    cursorMax,
  };
}

/** Mirror a letter index across the palindrome center. */
export function mirrorIndex(letterIndex: number, letterCount: number): number {
  return letterCount - 1 - letterIndex;
}

/** Mask the punctuated text: letters → "_", everything else passes
   through. Same formula as bin/sync-puzzles' Python regex. */
export function buildMask(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    out += LETTER_RE.test(ch) ? "_" : ch;
  }
  return out;
}
